export enum ScreenSize {
  DESKTOP_L = 1394,
  DESKTOP = 1088,
  TABLET = 936,
  MOBILE = 578,
  MOBILE_S = 408,
}

// 1394 - infinity - DESKTOP_L
// 1088 - 1394 - DESKTOP
// 936 - 1088 - TABLET
// 578 - 936 - MOBILE
// 408 - 578 - MOBILE_S
// 0 - 408 - MOBILE_XS

export const padding = 40;
export const paddingMob = 16;
export const doublePadding = padding * 2;

export const device = {
  desktopL: `min-width: ${ScreenSize.DESKTOP_L + doublePadding}px`,
  desktop: `max-width: ${ScreenSize.DESKTOP_L + doublePadding}px`,
  tablet: `max-width: ${ScreenSize.DESKTOP + doublePadding}px`,
  mobile: `max-width: ${ScreenSize.TABLET + doublePadding}px`,
  mobileS: `max-width: ${ScreenSize.MOBILE + doublePadding}px`,
  mobileXS: `max-width: ${ScreenSize.MOBILE_S + doublePadding}px`,
};

export function hexToRGB(hex: string, alpha: number = 1) {
  const r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);

  return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')';
}

interface IFont {
  size: string;
  weight?: number;
  height?: string;
  spacing?: string;
}

const font = ({ size, weight, height, spacing }: IFont) => `
  font-size: ${size};
  font-weight: ${weight || 400};
  line-height: ${height};
  letter-spacing: ${spacing || 'normal'};
`;

const headline1 = font({
  size: '32px',
  weight: 700,
  height: '40px',
  spacing: '0.03em',
});
const headline2 = font({
  size: '22px',
  weight: 700,
  height: '28px',
  spacing: '0.02em',
});
const headline3 = font({
  size: '16px',
  weight: 700,
  height: '20px',
  spacing: '0.01em',
});
const headline4 = font({
  size: '14px',
  weight: 700,
  height: '18px',
  spacing: '0.00875em',
});
const headline1mobile = font({
  size: '28px',
  weight: 700,
  height: '36px',
  spacing: '0.03em',
});
const headline2mobile = font({
  size: '20px',
  weight: 700,
  height: '26px',
  spacing: '0.02em',
});
const headline3mobile = font({
  size: '16px',
  weight: 700,
  height: '20px',
  spacing: '0.16px',
});

const body1bold = font({
  size: '16px',
  weight: 700,
  height: '24px',
  spacing: '0.01em',
});
const body2bold = font({
  size: '14px',
  weight: 700,
  height: '20px',
  spacing: '0.01em',
});
const body1regular = font({ size: '16px', height: '24px' });
const body2regular = font({ size: '14px', height: '20px' });

const captionbold = font({ size: '17px', weight: 700, height: '17px' });
const caption1bold = font({ size: '12px', weight: 700, height: '14px' });
const caption2bold = font({ size: '12px', weight: 700, height: '18px' });
const caption3bold = font({ size: '12px', weight: 700, height: '12px' });
const caption4bold = font({ size: '10px', weight: 700, height: '12px' });
const caption5bold = font({ size: '7px', weight: 700, height: '8.4px' });
const caption1regular = font({ size: '12px', height: '18px' });
const caption2regular = font({ size: '10px', height: '14px' });
const caption3regular = font({ size: '7px', height: '8.4px' });

const profileTitle = font({
  size: '20px',
  weight: 700,
  height: '28px',
  spacing: '0.02em',
});

export const typography = {
  headline1: headline1,
  headline2: headline2,
  headline3: headline3,
  headline4: headline4,
  headline1mobile: headline1mobile,
  headline2mobile: headline2mobile,
  headline3mobile: headline3mobile,
  body1bold: body1bold,
  body2bold: body2bold,
  body1regular: body1regular,
  body2regular: body2regular,
  captionbold: captionbold,
  caption1bold: caption1bold,
  caption2bold: caption2bold,
  caption3bold: caption3bold,
  caption4bold: caption4bold,
  caption5bold: caption5bold,
  caption1regular: caption1regular,
  caption2regular: caption2regular,
  caption3regular: caption3regular,
  profileTitle: profileTitle,
};

interface IFlex {
  direction?: string;
  justifyContent?: string;
  alignItems?: string;
  gap?: string;
}

export const flex = ({ direction, justifyContent, alignItems, gap }: IFlex) => `
  display: flex;
  flex-direction: ${direction || 'row'};
  justify-content: ${justifyContent || 'flex-start'};
  align-items: ${alignItems || 'stretch'};
  gap: ${gap || 0};
`;
